import { request } from "../../../services/api"
import type { BaseApiResponse } from "../../../types/api.ts"
import type {
  Attachment,
  AttachmentFilterOptions
} from "../types/attachment.types.ts"
import type {
  AttachmentBulkResult,
  AttachmentId,
  AttachmentListData,
  AttachmentListParams,
  AttachmentMoveData,
  AttachmentRenameData,
  AttachmentUploadRequestData
} from "./services.types.ts"

const BASE_URL = `/project-management/attachments`

const PATHS = {
  list: `${BASE_URL}/`,
  filterOptions: `${BASE_URL}/filter_options/`,
  upload: `${BASE_URL}/upload/`,
  rename: (id: AttachmentId) => `${BASE_URL}/${id}/`,
  delete: (id: AttachmentId) => `${BASE_URL}/${id}/`,
  restoreFromTrash: (id: AttachmentId) => `${BASE_URL}/${id}/restore-from-trash/`,
  permanentDelete: (id: AttachmentId) => `${BASE_URL}/${id}/permanent-delete/`,
  emptyTrash: `${BASE_URL}/empty-trash/`,
  bulkRestoreFromTrash: `${BASE_URL}/bulk-restore-from-trash/`,
  bulkPermanentDelete: `${BASE_URL}/bulk-permanent-delete/`,
  changeStatus: (id: AttachmentId) => `${BASE_URL}/${id}/change-status/`
}

// GET /project-management/attachments/
export const getAttachments = async (params?: AttachmentListParams) => {
  return request.api.get<AttachmentListData, BaseApiResponse<unknown>>(
    PATHS.list,
    params
  )
}

// GET /project-management/attachments/filter_options/
export const getAttachmentsFilterOptions = async (params?: { project_id?: string }) => {
  return request.api.get<AttachmentFilterOptions, BaseApiResponse<unknown>>(
    PATHS.filterOptions,
    params
  )
}

// POST /project-management/attachments/upload/
export const uploadAttachment = async (
  data: AttachmentUploadRequestData,
  signal?: AbortSignal,
  onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void
) => {
  return request.api.post<
    BaseApiResponse<Attachment>,
    BaseApiResponse<unknown>
  >(PATHS.upload, data, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress,
    signal
  })
}

// PATCH /project-management/attachments/{id}/
export const renameAttachment = async (
  id: AttachmentId,
  data: AttachmentRenameData
) => {
  return request.api.patch<
    BaseApiResponse<Attachment>,
    BaseApiResponse<unknown>
  >(PATHS.rename(id), data)
}

// PATCH /project-management/attachments/{id}/ — folder placement only
export const moveAttachment = async (
  id: AttachmentId,
  data: AttachmentMoveData
) => {
  return request.api.patch<
    BaseApiResponse<Attachment>,
    BaseApiResponse<unknown>
  >(PATHS.rename(id), data)
}

// DELETE /project-management/attachments/{id}/
export const deleteAttachment = async (id: AttachmentId) => {
  return request.api.delete<BaseApiResponse<null>, BaseApiResponse<unknown>>(
    PATHS.delete(id)
  )
}

// POST /project-management/attachments/:id/restore-from-trash/
export const restoreAttachmentFromTrash = async (id: AttachmentId) => {
  return request.api.post<BaseApiResponse<Attachment>, BaseApiResponse<unknown>>(
    PATHS.restoreFromTrash(id)
  )
}

// POST /project-management/attachments/:id/permanent-delete/
export const permanentlyDeleteAttachment = async (id: AttachmentId) => {
  return request.api.post<BaseApiResponse<unknown>, BaseApiResponse<unknown>>(
    PATHS.permanentDelete(id)
  )
}

// POST /project-management/attachments/empty-trash/?project= — omit
// `projectId` for Global Trash's company-wide empty, which must send the
// explicit `scope=all` opt-in rather than relying on the param's absence
// (the backend refuses an empty-trash call naming neither).
export const emptyAttachmentsTrash = async (projectId?: string) => {
  return request.api.post<BaseApiResponse<{ deleted: number }>, BaseApiResponse<unknown>>(
    PATHS.emptyTrash,
    undefined,
    { params: projectId ? { project: projectId } : { scope: "all" } }
  )
}

// POST /project-management/attachments/bulk-restore-from-trash/
export const bulkRestoreAttachmentsFromTrash = async (ids: string[]) => {
  return request.api.post<AttachmentBulkResult, BaseApiResponse<unknown>>(
    PATHS.bulkRestoreFromTrash,
    { ids }
  )
}

// POST /project-management/attachments/bulk-permanent-delete/
export const bulkPermanentlyDeleteAttachments = async (ids: string[]) => {
  return request.api.post<AttachmentBulkResult, BaseApiResponse<unknown>>(
    PATHS.bulkPermanentDelete,
    { ids }
  )
}

// POST /project-management/attachments/:id/change-status/
export const changeAttachmentStatus = async (id: AttachmentId, status: string | null) => {
  return request.api.post<
    BaseApiResponse<Attachment>,
    BaseApiResponse<unknown>
  >(PATHS.changeStatus(id), { status })
}
